// src/pages/admin/FinancialYearPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
  Badge,
  InputGroup,
  FormControl,
  Pagination,
} from "react-bootstrap";
import { Search, X, ChevronLeft, ChevronRight } from "react-feather";
import { toast } from "react-toastify";

import "../../../css/master-common.css";

import {
  clearFinancialYearError,
  createFinancialYearThunk,
  deleteFinancialYearThunk,
  fetchFinancialYearsThunk,
  updateFinancialYearThunk,
  setCurrentFinancialYearThunk,
} from "../../../store/slices/financialYearSlice";

import { getPerm } from "../../../utils/perm";

const ITEMS_PER_PAGE = 10;

const Pill = ({ children }) => <Badge className="mm-pill">{children}</Badge>;

// ✅ Toast confirm helper (no window.confirm)
const confirmToast = (
  message,
  { yesText = "Yes", noText = "Cancel", variant = "danger", onYes, onNo } = {}
) => {
  toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{message}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className={`btn btn-${variant} btn-sm`}
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            {yesText}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            {noText}
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    }
  );
};

export default function FinancialYearPage() {
  const dispatch = useDispatch();
  const { list, loading, saving, error } = useSelector((s) => s.financialYears);

  // ✅ permissions
  const permsMap = useSelector((s) => s.permissions?.map) || {};
  const fyPerms = getPerm(permsMap, "financial_year") || {};
  const canCreate = !!fyPerms.can_create;
  const canUpdate = !!fyPerms.can_update;
  const canDelete = !!fyPerms.can_delete;

  // ✅ modal state
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [form, setForm] = useState({
    id: null,
    financial_year: "",
    current: "no", // yes/no
  });

  // ✅ search + pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchFinancialYearsThunk());
  }, [dispatch]);

  // ✅ toast errors (no Alert)
  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearFinancialYearError());
  }, [error, dispatch]);

  // ✅ filter by search
  const filteredRows = useMemo(() => {
    const rows = Array.isArray(list) ? list : [];
    const q = String(searchTerm || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) =>
      `${r.id ?? ""} ${r.financial_year ?? ""} ${r.current ?? ""}`.toLowerCase().includes(q)
    );
  }, [list, searchTerm]);

  // ✅ reset to page 1 on search
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ pagination
  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const clearSearch = () => setSearchTerm("");

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;

    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      <Pagination.Prev key="prev" onClick={() => goTo(currentPage - 1)} disabled={currentPage === 1}>
        <ChevronLeft size={16} className="me-1" />
        Prev
      </Pagination.Prev>
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => goTo(1)}>
          1
        </Pagination.Item>
      );
      if (!isMobile && startPage > 2) items.push(<Pagination.Ellipsis key="es" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => goTo(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ee" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => goTo(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next key="next" onClick={() => goTo(currentPage + 1)} disabled={currentPage === totalPages}>
        Next
        <ChevronRight size={16} className="ms-1" />
      </Pagination.Next>
    );

    return items;
  };

  // ✅ modal open/close
  const openCreate = () => {
    if (!canCreate) return toast.warning("No permission to create");
    setMode("create");
    setForm({ id: null, financial_year: "", current: "no" });
    setShowModal(true);
    dispatch(clearFinancialYearError());
  };

  const openEdit = (r) => {
    if (!canUpdate) return toast.warning("No permission to update");
    setMode("edit");
    setForm({
      id: r.id,
      financial_year: r.financial_year || "",
      current: String(r.current || "").toLowerCase() === "yes" ? "yes" : "no",
    });
    setShowModal(true);
    dispatch(clearFinancialYearError());
  };

  const closeModal = () => setShowModal(false);

  // ✅ submit
  const onSubmit = async (e) => {
    e.preventDefault();

    const fy = String(form.financial_year || "").trim();
    const current = form.current === "yes" ? "yes" : "no";
    if (!fy) return toast.warning("Financial year required");

    if (mode === "create" && !canCreate) return toast.warning("No create permission");
    if (mode === "edit" && !canUpdate) return toast.warning("No update permission");

    if (mode === "create") {
      const r = await dispatch(createFinancialYearThunk({ financial_year: fy, current }));
      if (createFinancialYearThunk.fulfilled.match(r)) {
        toast.success("Financial year created ✅");
        setShowModal(false);
        dispatch(fetchFinancialYearsThunk());
      } else {
        const msg = r?.payload?.message || r?.payload || r?.error?.message || "Create failed";
        toast.error(String(msg));
      }
    } else {
      const r = await dispatch(updateFinancialYearThunk({ id: form.id, financial_year: fy, current }));
      if (updateFinancialYearThunk.fulfilled.match(r)) {
        toast.success("Financial year updated ✅");
        setShowModal(false);
        dispatch(fetchFinancialYearsThunk());
      } else {
        const msg = r?.payload?.message || r?.payload || r?.error?.message || "Update failed";
        toast.error(String(msg));
      }
    }
  };

  const doDelete = async (r) => {
    const x = await dispatch(deleteFinancialYearThunk({ id: r.id }));
    if (deleteFinancialYearThunk.fulfilled.match(x)) {
      toast.success("Deleted 🗑️");
      dispatch(fetchFinancialYearsThunk());
    } else {
      const msg = x?.payload?.message || x?.payload || x?.error?.message || "Delete failed";
      toast.error(String(msg));
    }
  };

  const onDelete = (r) => {
    if (!canDelete) return toast.warning("No delete permission");
    confirmToast(`Delete financial year "${r.financial_year}"?`, {
      yesText: "Delete",
      noText: "Cancel",
      variant: "danger",
      onYes: () => doDelete(r),
    });
  };

  const doSetCurrent = async (r) => {
    const x = await dispatch(setCurrentFinancialYearThunk({ id: r.id }));
    if (setCurrentFinancialYearThunk.fulfilled.match(x)) {
      toast.success(`Set "${r.financial_year}" as Current ✅`);
      dispatch(fetchFinancialYearsThunk());
    } else {
      const msg = x?.payload?.message || x?.payload || x?.error?.message || "Set current failed";
      toast.error(String(msg));
    }
  };

  const onSetCurrent = (r) => {
    if (!canUpdate) return toast.warning("No update permission");
    confirmToast(`Set "${r.financial_year}" as Current year?`, {
      yesText: "Set Current",
      noText: "Cancel",
      variant: "primary",
      onYes: () => doSetCurrent(r),
    });
  };

  const showingFrom = filteredRows.length === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length);

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">Financial Year</h4>
          {/* <div className="mm-subtitle">Manage financial years (Add / Edit / Delete / Set Current)</div> */}
        </div>

        <Button
          className="mm-primary-btn"
          onClick={openCreate}
          disabled={!canCreate}
          title={!canCreate ? "You don't have Create permission" : ""}
        >
           Add Financial Year
        </Button>
      </div>

      {/* Toolbar (Search + Meta) */}
      <Card className="mm-card-soft mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={7}>
              <Form.Label className="form-label mb-1 fw-bold">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search size={18} />
                </InputGroup.Text>

                <FormControl
                  className="mm-inputgroup-control"
                  placeholder="Search by year / id / current..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {searchTerm ? (
                  <Button
                    className="mm-clear-btn d-flex align-items-center justify-content-center"
                    onClick={clearSearch}
                    type="button"
                    title="Clear"
                  >
                    <X size={16} />
                  </Button>
                ) : null}
              </InputGroup>
            </Col>

            <Col md={5} className="text-md-end text-muted small">
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                <>
                  Showing <b>{showingFrom}</b>-<b>{showingTo}</b> of <b>{filteredRows.length}</b>
                </>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-5 text-center text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading financial years...
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table hover className="mm-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>ID</th>
                      <th>Financial Year</th>
                      <th style={{ width: 140 }}>Current</th>
                      <th style={{ width: 320 }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-5">
                          {searchTerm ? (
                            <>
                              No results found for <b>"{searchTerm}"</b>
                              <div className="mt-2">
                                <Button variant="link" className="p-0" onClick={clearSearch}>
                                  Clear search
                                </Button>
                              </div>
                            </>
                          ) : (
                            "No financial years found"
                          )}
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td className="fw-bold">{r.financial_year}</td>
                          <td>
                            <Pill>{String(r.current || "").toLowerCase() === "yes" ? "YES" : "NO"}</Pill>
                          </td>

                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                className="mm-cancel-btn"
                                onClick={() => openEdit(r)}
                                disabled={saving || !canUpdate}
                                title={!canUpdate ? "No Update permission" : ""}
                              >
                                Edit
                              </Button>

                              <Button
                                size="sm"
                                className="mm-cancel-btn"
                                onClick={() => onSetCurrent(r)}
                                disabled={saving || !canUpdate}
                                title={!canUpdate ? "No Update permission" : ""}
                              >
                                Set Current
                              </Button>

                              <Button
                                size="sm"
                                className="mm-cancel-btn"
                                onClick={() => onDelete(r)}
                                disabled={saving || !canDelete}
                                title={!canDelete ? "No Delete permission" : ""}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 ? (
                <div className="d-flex justify-content-center py-3">
                  <Pagination className="mb-0 master-pagination">{getPaginationItems()}</Pagination>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} centered dialogClassName="master-modal">
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">{mode === "create" ? "Add Financial Year" : "Edit Financial Year"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Financial Year</Form.Label>
                  <Form.Control
                    className="mm-input"
                    value={form.financial_year}
                    onChange={(e) => setForm((p) => ({ ...p, financial_year: e.target.value }))}
                    placeholder="2024"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Current</Form.Label>
                  <Form.Select
                    className="mm-select"
                    value={form.current}
                    onChange={(e) => setForm((p) => ({ ...p, current: e.target.value }))}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Form.Select>

                  <div className="text-muted small mt-2">
                    If you select <b>Yes</b>, old current year becomes <b>No</b>.
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button className="mm-cancel-btn" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>

            <Button
              type="submit"
              className="mm-primary-btn"
              disabled={
                saving ||
                !String(form.financial_year || "").trim() ||
                (mode === "create" && !canCreate) ||
                (mode === "edit" && !canUpdate)
              }
            >
              {saving ? "Saving..." : mode === "create" ? "Save" : "Update"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}